import queryString from "query-string";
import {
	IS_SCENEVIEWER_CANDIDATE,
	IS_IOS,
	IS_AR_QUICKLOOK_CANDIDATE,
} from "@google/model-viewer/lib/constants";
import { debug } from "rtUtils/logger";
import ANALYTICS_EVENTS from "../analyticsEvents";
import getProductConfigFromQuery from "./queryProductConfig";
import serializeProductConfigs from "./serializeProductConfigs";
import { useConfigProductConfig } from "../hooks/config";

const MODEL_PROCESSOR_URL = process.env.MODEL_PROCESSOR;
const MODEL_PROCESSOR_IMMEDIATE_URL = process.env.MODEL_PROCESSOR_IMMEDIATE;

//keep a page level list of the requests that have been made to the model processor, so we only make it once per variant
const prevProcessorRequests = new Set();

export const issueModelProcessorRequest = async (modelUrl, productConfigs, cloudName) => {
	if (MODEL_PROCESSOR_IMMEDIATE_URL) {
		if (cloudName) {
			const pcSerialized = serializeProductConfigs(productConfigs);
			const prevKey = modelUrl + pcSerialized;

			if (pcSerialized && !prevProcessorRequests.has(prevKey)) {
				debug("sending request to model processing service for", { modelUrl, productConfigs });

				try {
					//we request a variant to be created asap and we dont care about the response,
					//this way its more likely it will be ready when the user needs it in the AR Viewer
					fetch(MODEL_PROCESSOR_IMMEDIATE_URL, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							"X-D8S-ROUTER": process.env.ROUTER_HEADER,
						},
						body: JSON.stringify({
							reqType: "productConfig",
							request: {
								type: "productConfig",
								model: modelUrl,
								productConfigs: pcSerialized,
								cloudName,
							},
						}),
					});

					prevProcessorRequests.add(prevKey);
				} catch (ex) {
					debug("encountered error when sending request to model processing service", ex);
				}
			}
		} else {
			debug("cloudName not provided, not using model processing service");
		}
	}
};

export const getIsArDevice = () => {
	return IS_SCENEVIEWER_CANDIDATE || IS_AR_QUICKLOOK_CANDIDATE;
};

export const getIsArSupported = (arMode = false) => {
	return getIsArDevice() ||
		//allow testing the AR View in dev
		(arMode && document.location.host.includes("localhost"));
};

export const getIsIos = () => IS_IOS;

export const parseArParams = (str) => {
	return str.split(",")
		.reduce((res, keyVal) => {
			const arr = keyVal.split("_");
			if (arr[0] && arr[1]) {
				res[arr[0]] = decodeURIComponent(arr[1]);
			}
			return res;
		}, {});
};

export const serializeArParams = (params) =>
	Object.entries(params)
		.map(([key, value]) => value !== "" ? `${key}_${encodeURIComponent(value)}` : null)
		.filter(Boolean)
		.join(",");



export const getModelUrlWithConfigFromSerializedConfigs = (src, serializedConfigs, cloudName) => {
	const lambdaUrl = MODEL_PROCESSOR_URL;
	return (lambdaUrl && src && serializedConfigs) ?
		`${lambdaUrl}&productConfigs=${serializedConfigs}&model=${src}&cloudName=${cloudName}` :
		src;
	}


export const getModelUrlWithConfigFromState = (src, cloudName) => {
	const productConfigs = useConfigProductConfig();
	const serializedConfigs = productConfigs ? serializeProductConfigs(productConfigs) : "";

	return getModelUrlWithConfigFromSerializedConfigs(src, serializedConfigs, cloudName);
}


export const getModelUrlWithConfigFromQuery = (src, cloudName) => {
	const qs = queryString.parse(location.search);
	const productConfigQuery = qs.productConfig;
	const pcs = getProductConfigFromQuery();

	return (pcs?.length > 0) ? getModelUrlWithConfigFromSerializedConfigs(src, productConfigQuery, cloudName) : src;
}

//get the URL for the Android Scene Viewer
export const getSceneViewerIntentUrl = ({ src, fallbackUrl, params, cloudName }) => {
	debug("CREATING SCENE VIEWER URL", { src, fallbackUrl, params });
	const modelUrl = getModelUrlWithConfigFromQuery(src, cloudName);
	debug("model processor url", {modelUrl});

	return [
		"intent://arvr.google.com/scene-viewer/1.2?file=",
		encodeURIComponent(modelUrl),
		"&mode=ar_only",
		`&enable_vertical_placement=${params?.placement === "wall" ? "true" : "false"}`,
		`&resizable=${params?.resizable === false ? "false" : "true"}`,
		params?.link ? `&link=${encodeURIComponent(params.link)}` : false,
		params?.title ? `&title=${encodeURIComponent(params.title)}` : false,
		"#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;",
		`S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};`,
		"end;",
	]
		.filter(Boolean)
		.join("");
};

export const initiateArView = async (modelUrl, fallbackUrl, params, renderingApi, cloudName, triggerAnalyticsEvent = null) => {
	const anchor = document.createElement("a");
	const isIos = getIsIos();

	let img;
	let objectUrl;
	let exportedUrl = modelUrl;

	if (isIos) {
		//iOS Quick Look
		const arraybuffer = await renderingApi.exportUsdz();
		const blob = new Blob([arraybuffer], {
			type: "model/vnd.usdz+zip",
		});

		objectUrl = URL.createObjectURL(blob);
		exportedUrl = new URL(objectUrl, self.location.toString()).toString();

		if (params?.resizable === false) {
			if (exportedUrl.hash) {
				modelUrl.hash += "&";
			}
			modelUrl.hash += "allowsContentScaling=0";
		}

		anchor.setAttribute("rel", "ar");
		img = document.createElement("img");
		anchor.appendChild(img);
		anchor.setAttribute("download", "model.usdz");
	} else {
		//Android Scene-Viewer
		exportedUrl = getSceneViewerIntentUrl({
			src: exportedUrl,
			fallbackUrl,
			params,
			cloudName,
		});


		//TODO: Handle fail to show AR in Android
		// const undoHashChange = () => {
		// 	if (self.location.hash === noArViewerSigil) {
		// 		isSceneViewerBlocked = true;
		// 		// The new history will be the current URL with a new hash.
		// 		// Go back one step so that we reset to the expected URL.
		// 		// NOTE(cdata): this should not invoke any browser-level navigation
		// 		// because hash-only changes modify the URL in-place without
		// 		// navigating:
		// 		self.history.back();
		// 		console.warn('Error while trying to present in AR with Scene Viewer');
		// 		console.warn('Falling back to next ar-mode');
		// 		this[$selectARMode]();
		// 		// Would be nice to activateAR() here, but webXR fails due to not
		// 		// seeing a user activation.
		// 	}
		// };
		// self.addEventListener('hashchange', undoHashChange, {once: true});
	}

	triggerAnalyticsEvent?.(ANALYTICS_EVENTS.AR_INTERACTION, {
		info: "init-ar-view",
		deviceType: isIos ? "iOS" : "android",
		modelUrl,
		fallbackUrl,
		params,
	});

	anchor.setAttribute("href", exportedUrl);
	anchor.click();

	if (img) {
		anchor.removeChild(img);
	}
};
